import {
  Box,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  useTheme,
  Card,
  TableCell,
  Icon,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import AppButton from "../buttons/AppButton";
import AppTableHeaderCell from "../pages/AppTableHeaderCell";
import AppTableRowCell from "../pages/AppTableRowCell";
import CustomTypography from "../form/CustomTypography";
import { useState } from "react";
import { OrderServices } from "../../../../models/Order/OrderProduct";
import { observer } from "mobx-react";
import pencil_uderline from "../../../../assets/pencil_uderline.png";
import SingleInput from "../pop-up/SingleInput";
import { useSearchParams } from "react-router-dom";
import { InvoiceService } from "../../../../models/Invoice/Invoice";
import { useStores } from "../../../../use-stores";

interface OrderServiceTableProps {
  services?: InvoiceService[];
  padding?: string;
  boxShadow?: string;
  updateQuantity?: (
    id: number,
    custom_name: string,
    value: number,
    field: string
  ) => void;
  handleDeleteService: (id: number, custom_name: string) => void;
  toggleServicesPopup: () => void;
  canUpdateOfferPrice?: boolean;
  canUpdateSellingPrice?: boolean;
  canChangeQuantity?: boolean;
  canDeleteService?: boolean;
  canAddService?: boolean;
  canSeeTaxes?: boolean;
  canSeeAmount?: boolean;
  canShowMrp?: boolean;
}

const OrderServiceTable: React.FC<OrderServiceTableProps> = ({
  services,
  padding,
  boxShadow,
  updateQuantity,
  handleDeleteService,
  toggleServicesPopup,
  canUpdateOfferPrice = true,
  canUpdateSellingPrice = true,
  canChangeQuantity = true,
  canDeleteService = true,
  canAddService = true,
  canSeeTaxes = true,
  canSeeAmount = true,
  canShowMrp = true,
}) => {
  const theme = useTheme();
  const [searchParams] = useSearchParams();
  const invoiceId = searchParams.get("invoice-id");
  const [editableField, setEditableField] = useState<{
    productId: number | undefined;
    custom_name: string | undefined;
    field: string | undefined;
  }>({
    productId: undefined,
    custom_name: undefined,
    field: undefined,
  });

  const { invoiceStore } = useStores();

  const [modalOpen, setModalOpen] = useState(false);
  const [modalValue, setModalValue] = useState("");

  const handleEditClick = (
    productId: number,
    custom_name: string,
    field: string,
    currentValue: string
  ) => {
    setEditableField({ productId, custom_name, field });
    setModalValue(currentValue);
    setModalOpen(true);
  };

  const handleModalSubmit = (value: string) => {
    if (!editableField.productId || !editableField.field) return;

    if (editableField.field) {
      updateField(
        editableField.productId,
        editableField.custom_name,
        Number(value),
        editableField.field
      );
    }

    setModalOpen(false);
    setEditableField({
      productId: undefined,
      custom_name: undefined,
      field: undefined,
    });
  };

  const updateField = (
    productId: number,
    custom_name: string | undefined,
    value: number,
    field: string
  ) => {
    if (updateQuantity)
      updateQuantity(productId, custom_name || "", value, field);
  };

  const formatFieldName = (fieldName: string): string => {
    return fieldName
      .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
      .replace(/^./, (str) => str.toUpperCase());
  };

  return (
    <Box sx={{ padding: padding || 3, borderRadius: 2 }}>
      <Card
        sx={{
          p: padding || 5,
          boxShadow:
            boxShadow ||
            "rgba(9, 30, 66, 0.25) 0px 1px 1px, rgba(9, 30, 66, 0.13) 0px 0px 1px 1px;",
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: 2,
          }}
        >
          <CustomTypography variant="heading4" fontWeight={700}>
            Services
          </CustomTypography>
          {canAddService && (
            <AppButton
              label="Add Service"
              startIcon={<AddIcon />}
              onClick={toggleServicesPopup}
              customStyles={{
                backgroundColor: "transparent",
                padding: 1,
                margin: 0,
                fontSize: "14px",
                color: theme.palette.customColors.pinkDark,
                "&:hover": {
                  backgroundColor: "transparent",
                  color: theme.palette.customColors.pinkDark,
                  boxShadow: "none",
                },
              }}
              disabled={
                Boolean(invoiceStore.invoiceDetails.id) &&
                !Boolean(invoiceStore?.invoiceRequest?.active)
              }
            />
          )}
        </Box>

        <TableContainer sx={{ borderRadius: "4px" }}>
          <Table sx={{ tableLayout: "auto", width: "100%" }}>
            <TableHead>
              <TableRow
                sx={{
                  backgroundColor:
                    theme.palette.customColors.grayBackGroundColor,
                  textAlign: "center",
                  borderRadius: "4px",
                  "& th": { borderBottom: "none" },
                }}
              >
                <AppTableHeaderCell
                  title="S.NO"
                  sx={{
                    color: theme.palette.customColors.tableGrayHeadingColor,
                    textAlign: "center",
                  }}
                />
                <AppTableHeaderCell
                  title="ID"
                  sx={{
                    color: theme.palette.customColors.tableGrayHeadingColor,
                    textAlign: "center",
                  }}
                />
                <AppTableHeaderCell
                  title="NAME"
                  sx={{
                    color: theme.palette.customColors.tableGrayHeadingColor,
                    textAlign: "center",
                  }}
                />
                <AppTableHeaderCell
                  title="CUSTOM LABEL"
                  sx={{
                    color: theme.palette.customColors.tableGrayHeadingColor,
                    textAlign: "center",
                  }}
                />
                <AppTableHeaderCell
                  title="SKU"
                  sx={{
                    color: theme.palette.customColors.tableGrayHeadingColor,
                    textAlign: "center",
                  }}
                />
                <AppTableHeaderCell
                  title="SAC CODE"
                  sx={{
                    color: theme.palette.customColors.tableGrayHeadingColor,
                    textAlign: "center",
                  }}
                />

                <AppTableHeaderCell
                  title="OFFER PRICE"
                  sx={{
                    color: theme.palette.customColors.tableGrayHeadingColor,
                    textAlign: "center",
                  }}
                />

                <AppTableHeaderCell
                  title="SELLING PRICE"
                  sx={{
                    color: theme.palette.customColors.tableGrayHeadingColor,
                    textAlign: "center",
                  }}
                />
                <AppTableHeaderCell
                  title="QUANTITY"
                  sx={{
                    color: theme.palette.customColors.tableGrayHeadingColor,
                    textAlign: "center",
                  }}
                />
                <AppTableHeaderCell
                  title="GST%"
                  sx={{
                    color: theme.palette.customColors.tableGrayHeadingColor,
                    textAlign: "center",
                  }}
                />
                <AppTableHeaderCell
                  title="TAX"
                  sx={{
                    color: theme.palette.customColors.tableGrayHeadingColor,
                    textAlign: "center",
                  }}
                />

                {canShowMrp && (
                  <AppTableHeaderCell
                    title="MRP"
                    sx={{
                      color: theme.palette.customColors.tableGrayHeadingColor,
                      textAlign: "center",
                    }}
                  />
                )}

                <AppTableHeaderCell
                  title="AMOUNT"
                  sx={{
                    color: theme.palette.customColors.tableGrayHeadingColor,
                    textAlign: "center",
                  }}
                />
                {canDeleteService && (
                  <AppTableHeaderCell
                    title=""
                    sx={{
                      color: theme.palette.customColors.tableGrayHeadingColor,
                      position: "sticky",
                      zIndex: 10,
                      minWidth: "80px",
                      right: 0,
                      background:
                        theme.palette.customColors.grayBackGroundColor,
                    }}
                  />
                )}
              </TableRow>
            </TableHead>
            {!services || services.length === 0 ? (
              <TableBody>
                <TableRow>
                  <TableCell
                    colSpan={14}
                    sx={{
                      borderBottom: "none",
                      padding: 3,
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        height: "fit-content",
                        textAlign: "center",
                      }}
                    >
                      <CustomTypography
                        variant="body2"
                        fontWeight={700}
                        color="rgba(218, 218, 218, 1)"
                      >
                        NO SERVICES FOUND!
                      </CustomTypography>
                    </Box>
                  </TableCell>
                </TableRow>
              </TableBody>
            ) : (
              <TableBody>
                {services?.map((service, index) => (
                  <TableRow key={index}>
                    <AppTableRowCell>{index + 1}.</AppTableRowCell>
                    <AppTableRowCell>{service.id}</AppTableRowCell>
                    <AppTableRowCell
                      textAlign="left"
                      fontWeight={700}
                      minWidth="180px"
                    >
                      {service.name || "--"}
                    </AppTableRowCell>

                    <AppTableRowCell minWidth="80px">
                      {service.custom_name || "--"}
                    </AppTableRowCell>

                    <AppTableRowCell minWidth="80px">
                      {service.sku || "--"}
                    </AppTableRowCell>

                    <AppTableRowCell minWidth="80px">
                      {service.sac_code || "--"}
                    </AppTableRowCell>

                    <AppTableRowCell minWidth="80px">
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          gap: "3px",
                        }}
                      >
                        {service.unit_price}
                        {canUpdateOfferPrice && (
                          <IconButton
                            sx={{ width: "10px" }}
                            onClick={() =>
                              handleEditClick(
                                service?.service_id!,
                                service.custom_name ?? "",
                                "offerPrice",
                                String(service.unit_price)
                              )
                            }
                          >
                            <Icon sx={{ fontSize: "12px" }}>
                              <img src={pencil_uderline} alt="edit-icon" />
                            </Icon>
                          </IconButton>
                        )}
                      </Box>
                    </AppTableRowCell>

                    <AppTableRowCell minWidth="80px">
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          gap: "3px",
                        }}
                      >
                        {service.selling_price}
                      </Box>
                    </AppTableRowCell>

                    <AppTableRowCell minWidth="80px">
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          gap: "3px",
                        }}
                      >
                        {service.quantity}
                        {canChangeQuantity && (
                          <IconButton
                            sx={{ width: "10px" }}
                            onClick={() =>
                              handleEditClick(
                                service?.service_id!,
                                service.custom_name ?? "",
                                "quantity",
                                String(service.quantity)
                              )
                            }
                          >
                            <Icon sx={{ fontSize: "12px" }}>
                              <img src={pencil_uderline} alt="edit-icon" />
                            </Icon>
                          </IconButton>
                        )}
                      </Box>
                    </AppTableRowCell>

                    <AppTableRowCell minWidth="80px">
                      {service.gst_percentage || "--"}
                    </AppTableRowCell>

                    <AppTableRowCell minWidth="80px">
                      {service.taxes || "--"}
                    </AppTableRowCell>

                    {canShowMrp && (
                      <AppTableRowCell minWidth="80px">
                        {service.mrp || "--"}
                      </AppTableRowCell>
                    )}

                    <AppTableRowCell minWidth="80px">
                      {canSeeAmount
                        ? Math.round(
                            (service?.amount! + Number.EPSILON) * 100
                          ) / 100 || "--"
                        : "--"}
                    </AppTableRowCell>

                    {canDeleteService && (
                      <AppTableRowCell
                        minWidth="80px"
                        sx={{
                          padding: "6px",
                          paddingTop: "1rem",
                          paddingBottom: "1rem",
                          textAlign: "center",
                          fontSize: 12,
                          width: "10%",
                          position: "sticky",
                          right: 0,
                          zIndex: 8,
                          background: theme.palette.customColors.white,
                        }}
                      >
                        <IconButton
                          color="secondary"
                          sx={{
                            backgroundColor:
                              theme.palette.customColors.pinkLight,
                            width: 30,
                            height: 30,
                          }}
                          onClick={() =>
                            handleDeleteService(
                              service?.service_id!,
                              service.custom_name || ""
                            )
                          }
                          disabled={
                            Boolean(invoiceStore.invoiceDetails.id) &&
                            !Boolean(invoiceStore?.invoiceRequest?.active)
                          }
                        >
                          <DeleteIcon
                            sx={{
                              color: theme.palette.customColors.pinkDark,
                              width: 20,
                              height: 20,
                            }}
                          />
                        </IconButton>
                      </AppTableRowCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            )}
          </Table>
        </TableContainer>
        <SingleInput
          open={modalOpen}
          onClose={() => setModalOpen(false)}
          heading={`Edit ${
            editableField.field ? formatFieldName(editableField.field) : ""
          }`}
          content={`Enter value`}
          optionalText={`${
            editableField.productId
              ? services?.find(
                  (service) => service.id === editableField.productId
                )?.name
              : ""
          }`}
          type="number"
          initialValue={modalValue}
          onSubmit={handleModalSubmit}
        />
      </Card>
    </Box>
  );
};

export default observer(OrderServiceTable);
